import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Activity, Car, Users, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase text-gray-500">{label}</span>
      <Icon className={`h-4 w-4 text-${color || 'pink'}-500`} />
    </div>
    <div className="text-3xl font-bold">{value}</div>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </Card>
);

const AdminOpsLive = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);

  const fetchData = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.get('/admin/ops/live');
      setData(data);
    } catch (err) {
      toast({ title: 'Failed to load ops snapshot', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 15000);  // refresh every 15s
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-ops-live-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Activity className="h-6 w-6 text-pink-600" /> Live Operations</h1>
            <p className="text-sm text-gray-500">Real-time snapshot of active rides and online drivers (refreshes every 15s).</p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="ops-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>

        {loading && !data && <p className="text-gray-500">Loading...</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Car} label="Active rides" value={data.active_rides_count} color="pink" />
              <StatCard icon={Users} label="Drivers online" value={data.online_drivers_count} color="green" />
              <StatCard icon={CheckCircle2} label="Today completed" value={data.today_completed} color="emerald" sub={`${data.today_rides} requested`} />
              <StatCard icon={XCircle} label="Today cancelled" value={data.today_cancelled} color="red" sub={`Completion: ${data.completion_rate}%`} />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4">
                <h2 className="font-semibold mb-3">Online drivers ({data.online_drivers?.length || 0})</h2>
                <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="ops-online-drivers">
                  {(data.online_drivers || []).slice(0, 30).map(d => (
                    <div key={d.userId} className="flex items-center gap-2 text-sm py-1 border-b last:border-b-0">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      <span className="font-medium">{d.name || 'Driver'}</span>
                      <span className="text-xs text-gray-500">{d.vehicleType} {d.vehiclePlate}</span>
                    </div>
                  ))}
                  {(!data.online_drivers || data.online_drivers.length === 0) && (
                    <p className="text-sm text-gray-500">No drivers online right now.</p>
                  )}
                </div>
              </Card>

              <Card className="p-4">
                <h2 className="font-semibold mb-3">Active rides ({data.live_rides?.length || 0})</h2>
                <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="ops-live-rides">
                  {(data.live_rides || []).slice(0, 30).map(r => (
                    <div key={r.id} className="text-sm py-1 border-b last:border-b-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{r.pickup} → {r.dropoff}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700">{r.status}</span>
                      </div>
                    </div>
                  ))}
                  {(!data.live_rides || data.live_rides.length === 0) && (
                    <p className="text-sm text-gray-500">No active rides at the moment.</p>
                  )}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOpsLive;
